import { MinimizedCallBanner } from '@act/react-components/member-page/Calls/MinimizedCallBanner';
import { MinimizedCallBannerDock } from '@act/react-components/member-page/Calls/MinimizedCallBannerDock';
import { useMinimizedCallBannerPresentation } from '@act/react-components/member-page/Calls/hooks/useMinimizedCallBannerPresentation.hook';
import { useMinimizedCallRestore } from '@act/react-components/member-page/Calls/hooks/useMinimizedCallRestore.hook';
import { getMinimizedDockDismissAction } from '@act/react-components/member-page/Calls/minimizedCallDockDismiss.util';
import { clearMinimizeNavigation } from '@act/react-components/member-page/Calls/callMinimizeNavigation.util';
import { performClearDockOnlyTeardown } from '@act/react-components/member-page/Calls/clearDockOnlyTeardown.util';
import { getCallFullScreenSaveAndClose } from '@act/react-components/member-page/Calls/FullScreenSaveClose.utils';
import { useInteractionsDialog } from '@act/react-components/dialog-forms/hooks/useInteractionsDialog.hook';
import { useEffect } from 'react';
import { actingUserAppSelectors, callsAppSelectors } from 'redux/redux';
import {
  callsSlice,
  setMinimizedCallId,
} from 'redux/slices/callsSlice.slice';
import { selectMinimizedCallId } from 'redux/slices/callsSliceSelectors.util';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';

/**
 * App-level minimized call dock. Persists across route changes while a call is minimized.
 *
 * @returns Portaled minimized call banner or null.
 */
export const MinimizedCallDisplay = () => {
  const dispatch = useAppDispatch();
  const interactionsDialog = useInteractionsDialog();
  const minimizedCallId = useAppSelector(selectMinimizedCallId);
  const call = callsAppSelectors.useSelectCallById(minimizedCallId ?? -1);
  const activeCall = minimizedCallId != null ? call : undefined;
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const { callTitle, phoneLabel } =
    useMinimizedCallBannerPresentation(activeCall);
  const restoreCallView = useMinimizedCallRestore(activeCall);

  const dismissAction = getMinimizedDockDismissAction(
    activeCall,
    actingUser?.id
  );

  useEffect(() => {
    if (minimizedCallId == null || !activeCall || dismissAction == null) {
      return;
    }

    if (dismissAction === 'restoreFullscreen') {
      restoreCallView();
      return;
    }

    // Guide left a continuing conference: dismiss dock/JIT and unmount call UI
    // without sliding fullscreen back up.
    performClearDockOnlyTeardown({
      callDialogIsMounted: getCallFullScreenSaveAndClose() != null,
      clearMinimizeNavigation,
      closeCallDialog: () => {
        interactionsDialog.handleDialogClose();
      },
      clearMinimizedCallId: () => {
        dispatch(setMinimizedCallId(null));
      },
      removeJustInTimeCall: () => {
        dispatch(callsSlice.actions.removeJustInTimeCall(minimizedCallId));
      },
    });
  }, [
    activeCall,
    dismissAction,
    dispatch,
    interactionsDialog,
    minimizedCallId,
    restoreCallView,
  ]);

  if (minimizedCallId == null || !activeCall || dismissAction != null) {
    return null;
  }

  return (
    <MinimizedCallBannerDock visible>
      <MinimizedCallBanner
        call={activeCall}
        callTitle={callTitle}
        phoneLabel={phoneLabel}
        onRestore={restoreCallView}
      />
    </MinimizedCallBannerDock>
  );
};
